import type { UserPreferencesResponse } from "./notification";
import type { Access } from "./role";
import type { PresenceStatus } from "@/features/presence/types";

// Organization roles
export const ORG_ROLE = {
  ADMIN: "organization:admin",
  MEMBER: "organization:member",
} as const;

export type OrgRole = (typeof ORG_ROLE)[keyof typeof ORG_ROLE];

// Workspace roles
export const WORKSPACE_ROLE = {
  ADMIN: "workspace:admin",
  MEMBER: "workspace:member",
  AGENT: "workspace:agent",
  MARKETING: "workspace:marketing",
  FINANCE: "workspace:finance",
} as const;

export type WorkspaceRole =
  (typeof WORKSPACE_ROLE)[keyof typeof WORKSPACE_ROLE];

// @deprecated - Use ORG_ROLE or WORKSPACE_ROLE instead
export const USER_ROLE = {
  ADMIN: "admin",
  MEMBER: "member",
  AGENT: "agent",
} as const;

// @deprecated - Use OrgRole or WorkspaceRole instead
export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export const USER_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  EXPIRED: "expired",
} as const;

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

// Interfaces
/** The user's own settings; an update merges into it. */
export interface UserMeta {
  /** Where Echo drafts on my conversations, the composer opens on Ask Echo. */
  composerStartsOnEcho?: boolean;
}

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  imageUrl: string | null;
  externalId: string;
  organizationId: string;
  role: OrgRole;
  status?: UserStatus;
  invitationId?: string; // Present for PENDING and EXPIRED users
  title?: string;
  timezone?: string;
  language?: string;
  meta?: UserMeta;
  location?: string;
  biography?: string;
  updatedAt: Date;
  createdAt: Date;
  deletedAt: null;
  presence: UserPresence;
  workspaces: UserWorkspace[];
  /** Only on `/users/me`; absent from an older backend. */
  access?: Access | null;
  /** Only on `/users/me`: the live channels to join besides the workspace's. Absent from an older backend. */
  realtime?: RealtimeChannels | null;
};

export type RealtimeChannels = {
  userChannel: string;
};

export type UserPresence = {
  status: PresenceStatus;
  lastSeenAt: string | null;
};

// User's workspace assignment with role
export type UserWorkspace = {
  id: string;
  name: string;
  role: WorkspaceRole;
  roleId?: string;
};

// User in workspace context (has workspace role instead of org role)
export type WorkspaceUser = Omit<User, "role" | "workspaces"> & {
  role: WorkspaceRole;
  /** A built-in role's id is its base value; a custom role's is a uuid. */
  roleId?: string | null;
  organizationRole: OrgRole;
  invitationId?: string; // Present for PENDING and EXPIRED users
};

export type WorkspaceInvitation = {
  id: string;
  /** The picked role's base. Legacy for the API when `roleId` is sent. */
  role: WorkspaceRole;
  /** A built-in role's base value or a custom role's uuid. */
  roleId?: string;
  /** Teams to add them to in this workspace. */
  teamIds?: string[];
};

export type UserInvitation = {
  firstName: string;
  lastName: string;
  email: string;
  role: OrgRole;
  workspaces: WorkspaceInvitation[];
};

export interface UpdateUserRoleVariables {
  id: string;
  role?: OrgRole;
  workspaces?: WorkspaceInvitation[];
}

export interface AddWorkspaceUserVariables {
  workspaceId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** The picked role's base. Only the mock reads it; the API takes `roleId`. */
  role: WorkspaceRole;
  /** A built-in role's base value or a custom role's uuid. */
  roleId: string;
}

export interface UpdateWorkspaceUserRoleVariables {
  workspaceId: string;
  userId: string;
  /** The picked role's base. Only the mock reads it; the API takes `roleId`. */
  role: WorkspaceRole;
  /** A built-in role's base value or a custom role's uuid. */
  roleId: string;
}

export interface RemoveWorkspaceUserVariables {
  workspaceId: string;
  userId: string;
}

export type UserPreferences = UserPreferencesResponse;
